import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FileDown } from "lucide-react";
import {
  DOWNLOAD_ATTENDANCE_REPORT,
  DOWNLOAD_TRAVEL_REPORT_HR,
} from "../../constants";
import axios from "axios";

const HRComponent = ({}) => {
  // Set default dates from 25th last month to 26th this month
  const defaultStartDate = dayjs().subtract(1, "month").date(25);
  const defaultEndDate = dayjs().date(26);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");
  const [alertTitle, setalertTitle] = useState("");

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  // Filter options
  const filterOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "this_week" },
    { label: "This Month", value: "this_month" },
    { label: "Last Month", value: "last_month" },
    { label: "Custom", value: "custom" },
  ];

  // Add a new state
  const [selectedFilter, setSelectedFilter] = useState("today");

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSelectedFilter(value);

    const today = dayjs();

    switch (value) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "yesterday":
        const yesterday = today.subtract(1, "day");
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case "this_week":
        setStartDate(today.startOf("week"));
        setEndDate(today.endOf("week"));
        break;
      case "this_month":
        setStartDate(today.startOf("month"));
        setEndDate(today.endOf("month"));
        break;
      case "last_month":
        const lastMonth = today.subtract(1, "month");
        setStartDate(lastMonth.startOf("month"));
        setEndDate(lastMonth.endOf("month"));
        break;
      case "custom":
        // leave current startDate and endDate as is
        break;
      default:
        break;
    }
  };

  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleCloseModal = () => {
    setIsDownloading(false);
  };

  const downloadFile = (url) => {
    setIsDownloading(true);
    setDownloadProgress(0);

    axios
      .get(url, {
        responseType: "blob",
        timeout: 60000,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const realProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setDownloadProgress(realProgress);
          }
        },
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;

        const contentDisposition = response.headers["content-disposition"];
        let fileName = "report.xlsx";
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename="(.+)"/);
          if (matches && matches.length > 1) {
            fileName = matches[1];
          }
        }
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setIsDownloading(false);
        setSeverity("success");
        setMessage("File downloaded successfully");
        setOpenSnackbar(true);
      })
      .catch((error) => {
        console.error("Download failed:", error);
        setIsDownloading(false);
        setSeverity("error");
        setMessage("Failed to download file");
        setOpenSnackbar(true);
      });
  };

  const handleDownloadTravelReport = () => {
    if (!startDate || !endDate) {
      alert("Please select both Start Date and End Date.");
      return;
    }
    const formattedStart = dayjs(startDate).format("YYYY-MM-DD");
    const formattedEnd = dayjs(endDate).format("YYYY-MM-DD");
    const url = `${DOWNLOAD_TRAVEL_REPORT_HR}?startDate=${formattedStart}&endDate=${formattedEnd}`;
    downloadFile(url);
  };

  const handleDownloadAttendanceReport = () => {
    if (!startDate || !endDate) {
      alert("Please select both Start Date and End Date.");
      return;
    }
    const formattedStart = dayjs(startDate).format("YYYY-MM-DD");
    const formattedEnd = dayjs(endDate).format("YYYY-MM-DD");
    const url = `${DOWNLOAD_ATTENDANCE_REPORT}?startDate=${formattedStart}&endDate=${formattedEnd}`;
    downloadFile(url);
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={8} xl={6}>
          <Card
            sx={{
              maxWidth: 500,
              margin: "auto",
              textAlign: "center",
              padding: 4,
              borderRadius: 4,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              backgroundColor: "#f9f9f9",
              marginTop: 3,
            }}
          >
            <Box sx={{ mb: 2, placeItems: "center" }}>
              <FileDown style={{ color: "#ff9800" }} size={64} />
            </Box>
            <CardContent>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Reports
              </Typography>

              {/* Filter Dropdown */}
              <Grid container spacing={2} sx={{ mt: 3 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Filter</InputLabel>
                    <Select
                      label="Filter"
                      value={selectedFilter}
                      onChange={handleFilterChange}
                    >
                      {filterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Show DatePickers only if Custom is selected */}
                {selectedFilter === "custom" && (
                  <>
                    <Grid item xs={6}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        format="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField fullWidth {...params} />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        format="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField fullWidth {...params} />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Buttons */}
              <Grid container spacing={2} sx={{ mt: 3 }}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => handleDownloadTravelReport()}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    Download Travel Report
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={() => handleDownloadAttendanceReport()}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    Download Attendance Report
                  </Button>
                </Grid>
              </Grid>

              
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={isDownloading}
        onClose={(event, reason) => {
          // Disable closing when clicking outside the modal or pressing 'Escape'
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseModal();
          }
        }}
      >
        <DialogTitle>File Download in Progress...</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "250px",
              minHeight: "150px",
            }}
          >
            {/* Circular Progress with larger size */}
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={80}
              thickness={4}
              sx={{ color: downloadProgress < 100 ? "#1976d2" : "#4caf50" }} // Progress color change
            />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
              {downloadProgress}%
            </Typography>
            {/* Optional text for completed state */}
            {downloadProgress === 100 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Download complete! Preparing your file...
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000} // Snackbar will auto hide after 6 seconds
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HRComponent;
